import { Users, ShieldCheck } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { useMatchLineups } from "@/hooks/useMatchLineups";
import type {
  ProviderLineup,
  ProviderLineupTeam,
  ProviderLineupPlayer,
} from "@/lib/sports/providers/types";
import type { MatchLifecycle } from "@/lib/dataPolicy";

interface Props {
  fixtureId: string | undefined;
  lifecycle: MatchLifecycle;
  /** Optional fallback labels (flag/team codes) shown before official data arrives. */
  homeCode?: string;
  awayCode?: string;
}

/**
 * Real Data Phase 3 — Official Lineups module.
 *
 * Flow: API-Football → ApiFootballProvider → UnifiedSports → useMatchLineups → this UI.
 * No fabrication. When `lineup.official` is false or the team block is
 * missing, we render the lifecycle-appropriate placeholder from
 * `dataPolicy.ts` instead of a fake starting XI.
 */
export function OfficialLineups({ fixtureId, lifecycle, homeCode, awayCode }: Props) {
  const { t } = useLanguage();
  const { lineup, isLoading, isError } = useMatchLineups(fixtureId);

  const hasOfficial =
    !!lineup?.official && (lineup?.home?.startXI?.length ?? 0) > 0 && (lineup?.away?.startXI?.length ?? 0) > 0;

  return (
    <section
      className="rounded-2xl border border-border/40 glass p-3 sm:p-4"
      aria-label={t("lineups_title")}
    >
      <header className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-primary" aria-hidden />
          <h3 className="text-sm font-black tracking-tight">{t("lineups_title")}</h3>
        </div>
        {hasOfficial && (
          <span className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-1.5 py-0.5 text-[9px] font-black tracking-wider text-primary">
            <ShieldCheck className="h-3 w-3" aria-hidden />
            {t("lineups_source_api_football")}
          </span>
        )}
      </header>

      {isLoading ? (
        <PlaceholderRow label={t("lineups_loading")} homeCode={homeCode} awayCode={awayCode} />
      ) : !hasOfficial ? (
        <PlaceholderRow
          label={
            isError
              ? t("lineups_unavailable")
              : lifecycle === "SCHEDULED"
                ? t("lineups_not_announced")
                : t("lineups_unavailable")
          }
          homeCode={homeCode}
          awayCode={awayCode}
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <TeamColumn team={lineup!.home!} fallbackCode={homeCode} />
          <TeamColumn team={lineup!.away!} fallbackCode={awayCode} />
        </div>
      )}

      {hasOfficial && (lifecycle === "LIVE" || lifecycle === "FINISHED") && (
        <p className="mt-3 text-[10px] text-muted-foreground">
          {t("lineups_subs_in_timeline")}
        </p>
      )}
    </section>
  );
}

function PlaceholderRow({
  label,
  homeCode,
  awayCode,
}: { label: string; homeCode?: string; awayCode?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-border/40 bg-background/40 px-3 py-6 text-center">
      <p className="text-xs font-semibold text-muted-foreground">{label}</p>
      {(homeCode || awayCode) && (
        <p className="text-[10px] text-muted-foreground/70">
          {homeCode ?? "?"} — {awayCode ?? "?"}
        </p>
      )}
    </div>
  );
}

function TeamColumn({
  team,
  fallbackCode,
}: { team: ProviderLineupTeam; fallbackCode?: string }) {
  const { t } = useLanguage();
  return (
    <div className="rounded-xl border border-border/40 bg-background/60 p-2.5">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {team.teamLogo ? (
            <img
              src={team.teamLogo}
              alt=""
              className="h-5 w-5 shrink-0 rounded-sm object-contain"
              loading="lazy"
            />
          ) : (
            <span className="text-[10px] font-black text-muted-foreground">
              {fallbackCode ?? "?"}
            </span>
          )}
          <span className="truncate text-[11px] font-black tracking-tight">
            {team.teamName}
          </span>
        </div>
        {team.formation && (
          <span className="rounded-md bg-primary/10 px-1.5 py-0.5 text-[9px] font-black tracking-wider text-primary">
            {t("lineups_formation")}: {team.formation}
          </span>
        )}
      </div>

      {team.coach && (
        <p className="mb-2 text-[10px] text-muted-foreground">
          <span className="font-bold text-foreground/80">{t("lineups_coach")}:</span>{" "}
          {team.coach}
        </p>
      )}

      <PlayerList label={t("lineups_starting_xi")} players={team.startXI} />
      {team.substitutes.length > 0 && (
        <div className="mt-2">
          <PlayerList label={t("lineups_bench")} players={team.substitutes} muted />
        </div>
      )}
    </div>
  );
}

function PlayerList({
  label,
  players,
  muted,
}: { label: string; players: ProviderLineupPlayer[]; muted?: boolean }) {
  return (
    <div>
      <p className="mb-1 text-[9px] font-black uppercase tracking-widest text-muted-foreground">
        {label}
      </p>
      <ul className="space-y-0.5">
        {players.map((p) => (
          <li
            key={`${p.id}-${p.number}-${p.name}`}
            className={`flex items-center gap-2 text-[11px] ${muted ? "text-muted-foreground" : ""}`}
          >
            <span className="inline-flex h-4 w-6 shrink-0 items-center justify-center rounded bg-primary/10 text-[9px] font-black tabular-nums text-primary">
              {p.number || "-"}
            </span>
            {p.photo ? (
              <img
                src={p.photo}
                alt=""
                className="h-4 w-4 rounded-full object-cover"
                loading="lazy"
              />
            ) : null}
            <span className="truncate font-semibold">{p.name}</span>
            {p.captain && (
              <span className="ml-auto rounded bg-amber-500/20 px-1 py-0.5 text-[8px] font-black tracking-wider text-amber-500">
                C
              </span>
            )}
            {p.position && (
              <span
                className={`${p.captain ? "" : "ml-auto"} rounded bg-background/60 px-1 py-0.5 text-[8px] font-black tracking-wider text-muted-foreground`}
              >
                {p.position}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Type helper re-exported for tests. */
export type OfficialLineupsRenderInput = { lineup: ProviderLineup | undefined };